import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Agent } from '@/data/mockAgents';
import { Archive, Eye, Edit2, Camera, Copy, History, LockKeyhole } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { archiveAgent } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface AgentTableProps {
  agents: Agent[];
}

type ManagedAgent = Agent & {
  agent_category?: string | null;
  anam_persona_id?: string | null;
  enterprise_branch_access_loaded?: boolean;
  enterprise_branch_names?: string[];
  enterprise_can_duplicate?: boolean;
  enterprise_can_manage?: boolean;
  enterprise_duplicate_scope_label?: string;
  is_system?: boolean;
  source_agent_id?: string | null;
  source_agent_history?: {
    id: string;
    name: string;
    completions: number;
    avg_score: number | null;
    status: string;
  };
};

export function AgentTable({ agents }: AgentTableProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMasterTenant } = useTenant();
  const { hasEnterpriseHierarchy } = useOrganisation();
  const [deleteAgentState, setDeleteAgentState] = useState<Agent | null>(null);

  const isTenantAdmin = !!user && (user.roles.includes('admin') || user.roles.includes('company_admin')) && !isMasterTenant;
  const canEditAgent = (agent: ManagedAgent) => isMasterTenant || (
    agent.is_system !== true
    && (!hasEnterpriseHierarchy || agent.enterprise_can_manage === true)
  );
  const canDuplicateToScope = (agent: ManagedAgent) => hasEnterpriseHierarchy
    && agent.enterprise_can_duplicate === true;

  const handleDelete = async () => {
    if (!deleteAgentState) return;
    
    try {
      await archiveAgent(deleteAgentState.id, hasEnterpriseHierarchy);
      setDeleteAgentState(null);
      window.location.reload();
    } catch (error) {
      console.error('Delete failed:', error);
      setDeleteAgentState(null);
    }
  };

  return (
    <>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted hover:bg-muted">
              <TableHead className="font-semibold text-foreground">Agent</TableHead>
              <TableHead className="font-semibold text-foreground">Access</TableHead>
              <TableHead className="font-semibold text-foreground">Type</TableHead>
              <TableHead className="font-semibold text-foreground">Platform</TableHead>
              <TableHead className="font-semibold text-foreground">Collection</TableHead>
              <TableHead className="font-semibold text-foreground">Specialty</TableHead>
              <TableHead className="font-semibold text-foreground">Completions</TableHead>
              <TableHead className="font-semibold text-foreground">Avg Score</TableHead>
              <TableHead className="font-semibold text-foreground">Status</TableHead>
              <TableHead className="text-right font-semibold text-foreground">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agents.map((agent) => {
              const managedAgent = agent as ManagedAgent;
              return (
              <TableRow key={agent.id} className="hover:bg-muted/50">
                <TableCell>
                  <div className="flex items-center gap-3">
                    {agent.image_url ? (
                      <img
                        src={agent.image_url}
                        alt={agent.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-subtle to-primary/10 flex items-center justify-center">
                        <span className="text-sm font-bold text-primary">{agent.initials}</span>
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-foreground">{agent.name}</div>
                      <div className="text-sm text-muted-foreground">{agent.title}</div>
                      {agent.bmi && <div className="text-xs text-muted-foreground">BMI: {agent.bmi}</div>}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex max-w-[220px] flex-wrap gap-1">
                    {managedAgent.is_system === true ? (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                        🌐 System
                      </span>
                    ) : managedAgent.enterprise_branch_access_loaded === true ? (
                      (managedAgent.enterprise_branch_names || []).length > 0 ? (
                        (managedAgent.enterprise_branch_names || []).map((branchName) => (
                          <span key={branchName} className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                            {branchName}
                          </span>
                        ))
                      ) : (
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                          All branches
                        </span>
                      )
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                        🏢 Company
                      </span>
                    )}
                    {hasEnterpriseHierarchy && managedAgent.is_system !== true && !canEditAgent(managedAgent) && (
                      <span
                        title={`Global agent — view only. Duplicate it into ${managedAgent.enterprise_duplicate_scope_label || 'your organisation scope'} to edit.`}
                        className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs font-semibold text-muted-foreground"
                      >
                        <LockKeyhole className="h-3 w-3" /> View only
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    agent.type === 'patient' 
                      ? 'bg-blue-500 text-white' 
                      : agent.type === 'examiner'
                      ? 'bg-purple-500 text-white'
                      : agent.type === 'coach'
                      ? 'bg-amber-500 text-white'
                      : 'bg-green-500 text-white'
                  }`}>
                    {agent.type === 'patient' ? '🏥 Patient' : agent.type === 'examiner' ? '👨‍⚕️ Examiner' : agent.type === 'coach' ? '🎯 Coach' : '💼 Customer'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                      agent.platform === 'anam' 
                        ? 'bg-primary/10 text-primary' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {agent.platform === 'anam' ? '🎥 Video' : '🎧 Audio'}
                    </span>
                    {agent.platform === 'anam' && managedAgent.anam_persona_id && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Camera size={12} />
                        <span>Video Avatar</span>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-foreground">{managedAgent.agent_category || '-'}</TableCell>
                <TableCell className="text-foreground">{agent.specialty || '-'}</TableCell>
                <TableCell className="text-foreground">
                  <div className="font-semibold">
                    {agent.completions ?? 0}{managedAgent.source_agent_id ? ' unit' : ''}
                  </div>
                  {managedAgent.source_agent_history
                    && (managedAgent.source_agent_history.completions > 0
                      || managedAgent.source_agent_history.avg_score !== null) && (
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/agents/${managedAgent.source_agent_history?.id}/view`)}
                      className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
                      title={`View retained history for ${managedAgent.source_agent_history.name}`}
                    >
                      <History className="h-3 w-3" />
                      {managedAgent.source_agent_history.completions} historical
                    </button>
                  )}
                </TableCell>
                <TableCell className="font-semibold text-primary">{agent.avgScore}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    agent.status === 'active' 
                      ? 'bg-success/10 text-success' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${agent.status === 'active' ? 'bg-success' : 'bg-muted-foreground'}`} />
                    {agent.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => navigate(`/simulation/${agent.id}`)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      title="View Simulation"
                    >
                      <Eye size={16} className="text-muted-foreground" />
                    </button>

                    {((isTenantAdmin && managedAgent.is_system === true) || canDuplicateToScope(managedAgent)) && (
                      <button
                        onClick={() => navigate(`/admin/agents/create?template=${agent.id}${user?.company_id ? `&company=${user.company_id}` : ''}`)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title={canDuplicateToScope(managedAgent)
                          ? `Duplicate to ${managedAgent.enterprise_duplicate_scope_label || 'your organisation scope'}`
                          : 'Duplicate'}
                      >
                        <Copy size={16} className="text-muted-foreground" />
                      </button>
                    )}

                    {canEditAgent(managedAgent) && (
                      <>
                        <button
                          onClick={() => navigate(`/admin/agents/${agent.id}/edit`)}
                          className="p-2 hover:bg-muted rounded-lg transition-colors"
                          title="Edit Agent"
                        >
                          <Edit2 size={16} className="text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => setDeleteAgentState(agent)}
                          className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                          title="Archive Agent"
                        >
                          <Archive size={16} className="text-destructive" />
                        </button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Archive confirmation */}
      {deleteAgentState && (
        <DeleteConfirmModal
          agentName={deleteAgentState.name}
          onConfirm={handleDelete}
          onCancel={() => setDeleteAgentState(null)}
          open={!!deleteAgentState}
        />
      )}
    </>
  );
}
